import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BRAND_NAME, CONTACTS } from "@/lib/constants";
import {
  Phone,
  Mail,
  Search,
  ShoppingCart,
  X,
  Heart,
  ChevronDown,
  Menu,
  User,
  LogOut,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/use-auth";
import logoHeader from "@/assets/logo-header.png";
import logoMark from "@/assets/logo-mark.png";
import { useFavorites } from "@/contexts/FavoritesContext";
import { CatalogDropdown } from "@/components/CatalogDropdown";
import { SearchDropdown } from "@/components/SearchDropdown";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface HeaderSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  onSubmit: () => void;
  placeholder: string;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
}

/**
 * Search field plus its suggestion list. Each instance owns its open state: the header keeps
 * a CSS-hidden desktop copy mounted on phones, and with shared state that copy's
 * click-outside handler closed the mobile list before a tapped suggestion could navigate.
 */
function HeaderSearch({
  query,
  onQueryChange,
  onSubmit,
  placeholder,
  className,
  inputClassName,
  autoFocus,
}: HeaderSearchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsOpen(false);
    onSubmit();
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={cn(
            "border-border/70 bg-secondary/70 pl-icon pr-icon focus-visible:ring-2 focus-visible:ring-primary",
            inputClassName,
          )}
          autoFocus={autoFocus}
        />
        {query && (
          <button
            type="button"
            aria-label="Очистить поиск"
            onClick={() => onQueryChange("")}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-primary transition-colors"
          >
            <X className="size-4" />
          </button>
        )}
      </form>
      <SearchDropdown
        searchQuery={query}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        containerRef={containerRef}
      />
    </div>
  );
}

/** Keeps header badges to at most three characters so they stay a pill, not a bar. */
const badgeCount = (n: number) => (n > 99 ? "99+" : String(n));

export function Header() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { getTotalItems } = useCart();
  // Badge shows units ordered, not distinct lines — `items.length` under-reports.
  // Units, not lines: a dealer's cart easily reaches three digits, hence the 99+ cap.
  const cartCount = getTotalItems();
  const { items: favoriteItems } = useFavorites();
  const { user } = useAuth();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const handleSearchSubmit = () => {
    setMobileSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/90 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        {/* Top bar - hidden on mobile */}
        <div className="surface-dark hidden border-b border-white/10 md:block">
          <div className="container flex h-10 items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a
                href={CONTACTS.phoneHref}
                className="flex items-center gap-2 transition-colors hover:text-primary"
              >
                <Phone className="size-4" />
                <span>{CONTACTS.phone}</span>
              </a>
              <a
                href={CONTACTS.emailHref}
                className="flex items-center gap-2 transition-colors hover:text-primary"
              >
                <Mail className="size-4" />
                <span>{CONTACTS.email}</span>
              </a>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-white/60">{CONTACTS.workingHours}</span>
              <Link to="/contacts" className="transition-colors hover:text-primary">
                Контакты
              </Link>
              <Link to="/delivery" className="transition-colors hover:text-primary">
                Доставка и оплата
              </Link>
            </div>
          </div>
        </div>

        {/* Main header */}
        <div className="container py-3 md:py-4">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Открыть меню"
                  className="shrink-0 border border-border/80 md:hidden"
                >
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[300px] border-r border-border/70 p-0"
                aria-describedby={undefined}
              >
                <div className="flex flex-col h-full">
                  <div className="border-b border-border bg-secondary/70 p-4">
                    <SheetTitle>Меню</SheetTitle>
                  </div>
                  {/* Groups separated by divide-y: each group owns its padding, so a divider can
                      never be squeezed by a sibling's spacing (space-y used to zero its margin). */}
                  <nav className="flex-1 divide-y divide-border overflow-y-auto px-4">
                    <div className="flex flex-col gap-1 py-4">
                      <Link
                        to="/catalog"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-md bg-primary px-3 py-3 font-semibold text-primary-foreground shadow-panel"
                      >
                        <Menu className="size-5" />
                        Каталог
                      </Link>
                      <Link
                        to="/favorites"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-secondary"
                      >
                        <Heart className="size-5" />
                        Избранное
                        {favoriteItems.length > 0 && (
                          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
                            {badgeCount(favoriteItems.length)}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/cart"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-secondary"
                      >
                        <ShoppingCart className="size-5" />
                        Корзина
                        {cartCount > 0 && (
                          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
                            {badgeCount(cartCount)}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/account"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-secondary"
                      >
                        <User className="size-5" />
                        Личный кабинет
                      </Link>
                    </div>
                    <div className="flex flex-col py-3">
                      <Link
                        to="/about"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex min-h-touch items-center px-3 text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        О компании
                      </Link>
                      <Link
                        to="/delivery"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex min-h-touch items-center px-3 text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        Доставка и оплата
                      </Link>
                      <Link
                        to="/contacts"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex min-h-touch items-center px-3 text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        Контакты
                      </Link>
                    </div>
                    {/* The only logout on a phone: the cabinet's own sheet (and its button) is gone. */}
                    {user && (
                      <div className="flex flex-col gap-1 py-3">
                        <p className="truncate px-3 text-xs text-secondary-foreground">
                          {user.name || user.email}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            logout();
                            setMobileMenuOpen(false);
                          }}
                          disabled={isLoggingOut}
                          className="flex items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-secondary w-full text-left disabled:opacity-50"
                        >
                          <LogOut className="size-5" />
                          {isLoggingOut ? "Выход..." : "Выйти"}
                        </button>
                      </div>
                    )}
                  </nav>
                  <div className="space-y-2 border-t border-border bg-secondary/50 p-4">
                    <a
                      href={CONTACTS.phoneHref}
                      className="flex min-h-touch items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Phone className="size-4" />
                      {CONTACTS.phone}
                    </a>
                    <p className="text-xs text-muted-foreground">{CONTACTS.workingHours}</p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Home link. Phones get the wolverine alone: at h-8 the full lockup's wordmark
                and tagline shrink to a few illegible pixels and squeeze the mark itself. */}
            <Link to="/" className="flex h-10 shrink-0 items-center">
              <picture>
                <source media="(min-width: 768px)" srcSet={logoHeader} />
                <img src={logoMark} alt={BRAND_NAME} className="h-8 md:h-14" />
              </picture>
            </Link>

            {/* Catalog Button - desktop only */}
            <div
              className="relative ml-2 md:ml-4 hidden md:block"
              onMouseEnter={() => setIsCatalogOpen(true)}
              onMouseLeave={() => setIsCatalogOpen(false)}
            >
              <Link
                to="/catalog"
                className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 font-semibold text-primary-foreground shadow-panel transition-colors hover:bg-primary-strong"
              >
                Каталог
                <ChevronDown
                  className={`size-4 transition-transform ${isCatalogOpen ? "rotate-180" : ""}`}
                />
              </Link>
              {isCatalogOpen && <CatalogDropdown />}
            </div>

            {/* Search - desktop */}
            <HeaderSearch
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onSubmit={handleSearchSubmit}
              placeholder="Поиск по артикулу или названию..."
              className="hidden flex-1 max-w-4xl md:block"
            />

            {/* Actions - Desktop */}
            <div className="hidden md:flex items-center gap-1 ml-auto">
              <Link
                to="/favorites"
                className="relative flex flex-col items-center gap-0.5 rounded-md px-3 py-2 transition-colors hover:bg-secondary"
              >
                <Heart className="size-5" />
                <span className="text-xs text-muted-foreground">Избранное</span>
                {favoriteItems.length > 0 && (
                  <span className="absolute top-0 right-1 min-w-5 h-5 px-1 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                    {badgeCount(favoriteItems.length)}
                  </span>
                )}
              </Link>
              <Link
                to="/cart"
                className="relative flex flex-col items-center gap-0.5 rounded-md px-3 py-2 transition-colors hover:bg-secondary"
              >
                <ShoppingCart className="size-5" />
                <span className="text-xs text-muted-foreground">Корзина</span>
                {cartCount > 0 && (
                  <span className="absolute top-0 right-1 min-w-5 h-5 px-1 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                    {badgeCount(cartCount)}
                  </span>
                )}
              </Link>

              {/* User Account */}
              <Link
                to="/account"
                className="flex flex-col items-center gap-0.5 rounded-md px-3 py-2 transition-colors hover:bg-secondary"
              >
                <User className="size-5" />
                <span className="text-xs text-muted-foreground">Кабинет</span>
              </Link>
            </div>

            {/* Actions - Mobile */}
            <div className="flex md:hidden items-center gap-2 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Поиск"
                className="relative border border-border/60"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              >
                <Search className="size-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="relative border border-border/60"
                asChild
              >
                <Link to="/cart" aria-label={`Корзина${cartCount > 0 ? `, ${cartCount}` : ""}`}>
                  <ShoppingCart className="size-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                      {badgeCount(cartCount)}
                    </span>
                  )}
                </Link>
              </Button>
            </div>
          </div>

          {/* Mobile search bar */}
          {mobileSearchOpen && (
            <HeaderSearch
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onSubmit={handleSearchSubmit}
              placeholder="Поиск..."
              className="mt-2 md:hidden"
              autoFocus
            />
          )}
        </div>
      </header>
    </>
  );
}
